import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Form,
  Input,
  Select,
  Button,
  Table,
  Modal,
  Tag,
  Space,
  Tabs,
  message,
  Tooltip,
  Collapse,
  Divider,
  Alert,
  Transfer,
  Switch,
  Drawer,
  Typography,
} from 'antd';
import {
  FileText,
  Plus,
  Eye,
  Save,
  Settings,
  Trash2,
  Edit,
  ChevronRight,
  Grid,
  List,
  Layout as LayoutIcon,
  Palette,
  Database,
  MapPin,
  ArrowRight,
} from 'lucide-react';
import { get_api_function, post_api_function } from '../../../utils/api';
import { toast } from 'react-toastify';
import MainCard from '@/components/MainCard';
import StatCard from '@/components/StatCard';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;

const { TextArea } = Input;
const { Panel } = Collapse;
const { Option } = Select;

const ReportTemplateConfig = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Dark mode colors
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const pageBgColor = isDark ? defaultColors.palette.gray[900] : '#f9fafb';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';
  const selectedBgColor = isDark ? '#1e3a5f' : '#eff6ff';
  const selectedBorderColor = isDark ? '#1e40af' : '#3b82f6';
  const drawerBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const nestedBgColor = isDark ? defaultColors.palette.gray[700] : '#f9fafb';
  const labelColor = isDark ? defaultColors.palette.gray[300] : '#374151';
  const [selectedTemplate, setSelectedTemplate] = useState('bmr');
  const [reportConfig, setReportConfig] = useState({
    name: 'Batch Manufacturing Record',
    category: 'Manufacturing',
    orientation: 'portrait',
    paperSize: 'A4',
  });

  // Sample API response fields that user can map
  const [availableFields] = useState([
    { key: 'batch_number', label: 'Batch Number', type: 'text' },
    { key: 'product_name', label: 'Product Name', type: 'text' },
    { key: 'recipe_name', label: 'Recipe Name', type: 'text' },
    { key: 'batch_size', label: 'Batch Size', type: 'number' },
    { key: 'manufacturing_date', label: 'Manufacturing Date', type: 'date' },
    { key: 'expiry_date', label: 'Expiry Date', type: 'date' },
    { key: 'operator_name', label: 'Operator Name', type: 'text' },
    { key: 'status', label: 'Status', type: 'text' },
    { key: 'created_by', label: 'Created By', type: 'text' },
    { key: 'process_count', label: 'Process Count', type: 'number' },
    { key: 'material_name', label: 'Material Name', type: 'text' },
    { key: 'quantity', label: 'Quantity', type: 'number' },
    { key: 'unit', label: 'Unit', type: 'text' },
    { key: 'lot_number', label: 'Lot Number', type: 'text' },
  ]);

  const [sections, setSections] = useState([
    {
      id: 1,
      name: 'Header Information',
      layout: 'grid',
      columns: 2,
      fields: [],
      enabled: true,
    },
    {
      id: 2,
      name: 'Materials',
      layout: 'table',
      columns: 4,
      fields: [],
      enabled: true,
    },
    {
      id: 3,
      name: 'Process Details',
      layout: 'table',
      columns: 3,
      fields: [],
      enabled: true,
    },
    {
      id: 4,
      name: 'Signatures',
      layout: 'grid',
      columns: 2,
      fields: [],
      enabled: true,
    },
  ]);

  const [activeSection, setActiveSection] = useState(1);
  const [fieldMapperVisible, setFieldMapperVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [styling, setStyling] = useState({
    headerColor: '#1890ff',
    fontFamily: 'Arial',
    fontSize: 12,
    borderColor: '#d9d9d9',
    showBorders: true,
    showPageNumbers: true,
  });

  const templates = [
    {
      id: 'bmr',
      name: 'Batch Manufacturing Record',
      description: 'Complete manufacturing documentation with materials and processes',
      icon: <FileText size={24} />,
      color: '#1890ff',
    },
    {
      id: 'release',
      name: 'Batch Release Record',
      description: 'Quality control and release approval documentation',
      icon: <FileText size={24} />,
      color: '#52c41a',
    },
    {
      id: 'deviation',
      name: 'Deviation Report',
      description: 'Non-conformance and investigation tracking',
      icon: <FileText size={24} />,
      color: '#ff4d4f',
    },
    {
      id: 'equipment',
      name: 'Equipment Log',
      description: 'Equipment usage and maintenance records',
      icon: <FileText size={24} />,
      color: '#722ed1',
    },
  ];

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find((t) => t.id === templateId);
    setReportConfig({ ...reportConfig, name: template.name });
    message.success(`Template "${template.name}" selected`);
  };

  const handleAddField = (sectionId, field) => {
    setSections(
      sections.map((section) => {
        if (section.id === sectionId) {
          const exists = section.fields.some((f) => f.key === field.key);
          if (exists) {
            message.warning('Field already added to this section');
            return section;
          }
          return {
            ...section,
            fields: [...section.fields, { ...field, displayLabel: field.label }],
          };
        }
        return section;
      })
    );
    message.success(`Field "${field.label}" added to section`);
  };

  const handleRemoveField = (sectionId, fieldKey) => {
    setSections(
      sections.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            fields: section.fields.filter((f) => f.key !== fieldKey),
          };
        }
        return section;
      })
    );
  };

  const handleFieldLabelChange = (sectionId, fieldKey, newLabel) => {
    setSections(
      sections.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            fields: section.fields.map((f) =>
              f.key === fieldKey ? { ...f, displayLabel: newLabel } : f
            ),
          };
        }
        return section;
      })
    );
  };

  const handleSectionToggle = (sectionId) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId ? { ...section, enabled: !section.enabled } : section
      )
    );
  };

  const handleLayoutChange = (sectionId, layout) => {
    setSections(
      sections.map((section) => (section.id === sectionId ? { ...section, layout } : section))
    );
  };

  const generatePreview = async () => {
    setLoading(true);
    try {
      // Fetch completed batch data
      const response = await get_api_function(
        '/bp/get_sample_batch_data',
        (res) => {
          if (res.data.status === 'success') {
            const batchData = res.data.data;

            // Generate enhanced HTML preview with real data
            let html = `
            <div style="font-family: ${styling.fontFamily}; font-size: ${
              styling.fontSize
            }px; padding: 30px; background: white; box-shadow: 0 2px 8px rgba(0,0,0,0.1); max-width: 1200px; margin: 0 auto;">
              <!-- Header -->
              <div style="text-align: center; margin-bottom: 40px; border-bottom: 4px solid ${
                styling.headerColor
              }; padding-bottom: 20px; background: linear-gradient(135deg, ${
              styling.headerColor
            }15 0%, white 100%); padding: 20px; border-radius: 8px 8px 0 0;">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
                  <div style="font-size: 11px; color: #666; text-align: left;">
                    <strong>Report ID:</strong> RPT-${Date.now()}<br/>
                    <strong>Generated:</strong> ${new Date().toLocaleDateString()}
                  </div>
                  <div style="width: 60px; height: 60px; background: ${
                    styling.headerColor
                  }; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 24px; font-weight: bold;">
                    &#10003;
                  </div>
                </div>
                <h1 style="color: ${
                  styling.headerColor
                }; margin: 10px 0; font-size: 28px; font-weight: 700; letter-spacing: -0.5px;">${
              reportConfig.name
            }</h1>
                <p style="color: #666; margin: 5px 0; font-size: 14px; text-transform: uppercase; letter-spacing: 1px; font-weight: 500;">${
                  reportConfig.category
                }</p>
              </div>
          `;

            // Generate sections with real data
            sections
              .filter((section) => section.enabled && section.fields.length > 0)
              .forEach((section, sectionIndex) => {
                html += `
                <div style="margin-bottom: 35px; page-break-inside: avoid;">
                  <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 15px;">
                    <div style="width: 35px; height: 35px; background: ${
                      styling.headerColor
                    }; color: white; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 16px;">
                      ${sectionIndex + 1}
                    </div>
                    <h3 style="color: ${
                      styling.headerColor
                    }; margin: 0; font-size: 20px; font-weight: 600; flex: 1;">${section.name}</h3>
                  </div>
                  <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; ${
                    styling.showBorders ? 'border: 2px solid ' + styling.borderColor : ''
                  }">
              `;

                if (section.layout === 'grid') {
                  html += `<div style="display: grid; grid-template-columns: repeat(${section.columns}, 1fr); gap: 20px;">`;
                  section.fields.forEach((field) => {
                    const value = batchData[field.key] || 'N/A';
                    html += `
                    <div style="padding: 15px; background: white; border-radius: 6px; ${
                      styling.showBorders ? 'border: 1px solid ' + styling.borderColor : ''
                    } box-shadow: 0 1px 3px rgba(0,0,0,0.08);">
                      <div style="font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px; font-weight: 600;">${
                        field.displayLabel
                      }</div>
                      <div style="font-size: 15px; color: #333; font-weight: 600;">${value}</div>
                    </div>
                  `;
                  });
                  html += '</div>';
                } else if (section.layout === 'table') {
                  html += `
                  <table style="width: 100%; border-collapse: collapse; background: white; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.08);">
                    <thead>
                      <tr style="background: ${styling.headerColor}; color: white;">
                        ${section.fields
                          .map(
                            (f) =>
                              `<th style="padding: 14px 16px; text-align: left; font-weight: 600; font-size: 13px; text-transform: uppercase; letter-spacing: 0.5px; border-right: 1px solid rgba(255,255,255,0.1);">${f.displayLabel}</th>`
                          )
                          .join('')}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        ${section.fields
                          .map((f) => {
                            const value = batchData[f.key] || 'N/A';
                            return `<td style="padding: 14px 16px; border-bottom: 1px solid ${styling.borderColor}; border-right: 1px solid ${styling.borderColor}; color: #333; font-weight: 500;">${value}</td>`;
                          })
                          .join('')}
                      </tr>
                    </tbody>
                  </table>
                `;
                } else {
                  section.fields.forEach((field, fieldIndex) => {
                    const value = batchData[field.key] || 'N/A';
                    html += `
                    <div style="margin: ${
                      fieldIndex > 0 ? '12px' : '0'
                    } 0; padding: 12px 15px; background: white; border-radius: 6px; ${
                      styling.showBorders ? 'border-left: 3px solid ' + styling.headerColor : ''
                    } display: flex; justify-content: space-between; align-items: center; box-shadow: 0 1px 2px rgba(0,0,0,0.05);">
                      <strong style="color: #555; font-size: 14px; font-weight: 600;">${
                        field.displayLabel
                      }:</strong>
                      <span style="color: #333; font-size: 14px; font-weight: 500;">${value}</span>
                    </div>
                  `;
                  });
                }

                html += `
                  </div>
                </div>
              `;
              });

            // Footer
            html += `
              <div style="margin-top: 50px; padding-top: 20px; border-top: 2px solid #e5e7eb;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <div style="font-size: 11px; color: #888;">
                    <strong>Report Configuration:</strong><br/>
                    Paper: ${reportConfig.paperSize} | Orientation: ${reportConfig.orientation}
                  </div>
                  ${
                    styling.showPageNumbers
                      ? `
                    <div style="font-size: 12px; color: #666; font-weight: 600;">
                      Page 1 of 1
                    </div>
                  `
                      : ''
                  }
                </div>
                <div style="text-align: center; margin-top: 15px; padding-top: 15px; border-top: 1px dashed #ddd; font-size: 10px; color: #999;">
                  This is a system-generated report. For official use only. | Generated: ${new Date().toLocaleString()}
                </div>
              </div>
            </div>
          `;

            setPreviewHtml(html);
            setPreviewVisible(true);
            message.success('Preview generated with real batch data');
          } else {
            message.error('Failed to fetch batch data');
          }
        },
        {}
      );
    } catch (error) {
      console.error('Error generating preview:', error);
      message.error('Error generating preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReport = () => {
    const reportData = {
      config: reportConfig,
      sections: sections.filter((s) => s.enabled),
      styling,
    };

    console.log('Report Configuration:', reportData);
    message.success('Report configuration saved successfully!');
    toast.success('Report template saved!');
  };

  const layoutColumns = [
    {
      title: 'Field',
      dataIndex: 'displayLabel',
      key: 'displayLabel',
      render: (text, record) => (
        <Input
          value={text}
          onChange={(e) => handleFieldLabelChange(activeSection, record.key, e.target.value)}
          size="small"
          placeholder="Display Label"
        />
      ),
    },
    {
      title: 'API Field',
      dataIndex: 'key',
      key: 'key',
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (text) => <Tag>{text}</Tag>,
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<Trash2 size={20} />}
          onClick={() => handleRemoveField(activeSection, record.key)}
        />
      ),
    },
  ];

  return (
    <div className="p-1.5 min-h-screen" style={{ backgroundColor: pageBgColor }}>
      <Card
        // className="shadow-sm"
        className="border border-gray-300 shadow-md"
        bordered={false}
        style={{ backgroundColor: cardBgColor }}
        title={
          <div className="flex items-center gap-2">
            <Icons.FileText className="w-7 h-7 text-blue-600" />
            <Title level={4} style={{ color: textColor, margin: 0, padding: 0 }}>
              {t('Report Template Config')}
            </Title>
          </div>
        }
        extra={
          <Space size="middle">
            <Button icon={<Eye size={16} />} onClick={generatePreview}>
              Preview
            </Button>
            <Button
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSaveReport}
              style={{
                backgroundColor: isDark ? '#0F172A' : defaultColors.primary,
                borderColor: isDark ? '#0F172A' : defaultColors.primary,
              }}
            >
              Save Report
            </Button>
          </Space>
        }
      >
        <div className="space-y-4">
          {/* Statistics */}
          <Row gutter={16} className="mb-6">
            <Col span={6}>
              <StatCard
                label="Total Sections"
                value={sections.filter((s) => s.enabled).length}
                icon={<LayoutIcon size={24} />}
                bgColor={isDark ? '#1e3a5f' : '#dbeafe'}
                iconColor={isDark ? '#60a5fa' : '#3b82f6'}
              />
            </Col>
            <Col span={6}>
              <StatCard
                label="Mapped Fields"
                value={sections.reduce((sum, s) => sum + s.fields.length, 0)}
                icon={<MapPin size={24} />}
                bgColor={isDark ? '#14532d' : '#d1fae5'}
                iconColor={isDark ? '#4ade80' : '#10b981'}
              />
            </Col>
            <Col span={6}>
              <StatCard
                label="Available Fields"
                value={availableFields.length}
                icon={<Database size={24} />}
                bgColor={isDark ? '#713f12' : '#fef3c7'}
                iconColor={isDark ? '#fbbf24' : '#f59e0b'}
              />
            </Col>
            <Col span={6}>
              <StatCard
                label="Active Template"
                value={1}
                icon={<FileText size={24} />}
                bgColor={isDark ? '#4c1d95' : '#e9d5ff'}
                iconColor={isDark ? '#c084fc' : '#9333ea'}
              />
            </Col>
          </Row>

          {/* Main Content */}
          <Row gutter={16}>
            {/* Left Panel - Template Selection */}
            <Col span={8}>
              <Card
                title="1. Select Report Template"
                bordered={false}
                className="mb-4"
                style={{ backgroundColor: cardBgColor }}
              >
                <div className="space-y-3">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className="p-4 border rounded-lg cursor-pointer transition-all"
                      style={{
                        backgroundColor:
                          selectedTemplate === template.id ? selectedBgColor : cardBgColor,
                        borderColor:
                          selectedTemplate === template.id ? selectedBorderColor : borderColor,
                      }}
                      onClick={() => handleTemplateSelect(template.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="p-2 rounded"
                          style={{ backgroundColor: template.color + '20', color: template.color }}
                        >
                          {template.icon}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold" style={{ color: textColor }}>
                            {template.name}
                          </h4>
                          <p className="text-sm mt-1" style={{ color: secondaryTextColor }}>
                            {template.description}
                          </p>
                        </div>
                        {selectedTemplate === template.id && <Tag color="blue">Selected</Tag>}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Report Settings */}
              <Card
                title="2. Report Settings"
                bordered={false}
                style={{ backgroundColor: cardBgColor }}
              >
                <Form layout="vertical" form={form}>
                  <Form.Item label={<span style={{ color: labelColor }}>Report Name</span>}>
                    <Input
                      value={reportConfig.name}
                      onChange={(e) => setReportConfig({ ...reportConfig, name: e.target.value })}
                    />
                  </Form.Item>
                  <Form.Item label={<span style={{ color: labelColor }}>Category</span>}>
                    <Select
                      value={reportConfig.category}
                      onChange={(value) => setReportConfig({ ...reportConfig, category: value })}
                    >
                      <Option value="Manufacturing">Manufacturing</Option>
                      <Option value="Quality Control">Quality Control</Option>
                      <Option value="Equipment">Equipment</Option>
                      <Option value="Safety">Safety</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item label={<span style={{ color: labelColor }}>Paper Size</span>}>
                    <Select
                      value={reportConfig.paperSize}
                      onChange={(value) => setReportConfig({ ...reportConfig, paperSize: value })}
                    >
                      <Option value="A4">A4</Option>
                      <Option value="Letter">Letter</Option>
                      <Option value="Legal">Legal</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item label={<span style={{ color: labelColor }}>Orientation</span>}>
                    <Select
                      value={reportConfig.orientation}
                      onChange={(value) => setReportConfig({ ...reportConfig, orientation: value })}
                    >
                      <Option value="portrait">Portrait</Option>
                      <Option value="landscape">Landscape</Option>
                    </Select>
                  </Form.Item>
                </Form>
              </Card>
            </Col>

            {/* Center Panel - Section Configuration */}
            <Col span={10}>
              <Card
                title="3. Configure Sections & Fields"
                extra={
                  <Button
                    type="primary"
                    icon={<Plus size={14} />}
                    onClick={() => setFieldMapperVisible(true)}
                  >
                    Map Fields
                  </Button>
                }
                bordered={false}
                style={{ backgroundColor: cardBgColor }}
              >
                <Collapse
                  accordion
                  activeKey={activeSection.toString()}
                  onChange={(key) => setActiveSection(Number(key))}
                >
                  {sections.map((section) => (
                    <Panel
                      header={
                        <div className="flex items-center justify-between w-full">
                          <Space>
                            <span className="font-medium">{section.name}</span>
                            <Tag color={section.layout === 'table' ? 'blue' : 'green'}>
                              {section.layout}
                            </Tag>
                            <Tag color="cyan">{section.fields.length} fields</Tag>
                          </Space>
                          <Switch
                            checked={section.enabled}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSectionToggle(section.id);
                            }}
                          />
                        </div>
                      }
                      key={section.id.toString()}
                    >
                      <div className="space-y-4">
                        {/* Layout Selection */}
                        <div>
                          <label
                            className="block text-sm font-medium mb-2"
                            style={{ color: labelColor }}
                          >
                            Layout Type
                          </label>
                          <Select
                            value={section.layout}
                            onChange={(value) => handleLayoutChange(section.id, value)}
                            className="w-full"
                          >
                            <Option value="grid">
                              <Space>
                                <Grid size={14} />
                                Grid Layout
                              </Space>
                            </Option>
                            <Option value="table">
                              <Space>
                                <LayoutIcon size={14} />
                                Table Layout
                              </Space>
                            </Option>
                            <Option value="list">
                              <Space>
                                <List size={14} />
                                List Layout
                              </Space>
                            </Option>
                          </Select>
                        </div>

                        {/* Fields Table */}
                        <div>
                          <label
                            className="block text-sm font-medium mb-2"
                            style={{ color: labelColor }}
                          >
                            Mapped Fields
                          </label>
                          {section.fields.length > 0 ? (
                            <Table
                              columns={layoutColumns}
                              dataSource={section.fields}
                              rowKey="key"
                              pagination={false}
                              size="small"
                              bordered
                            />
                          ) : (
                            <Alert
                              message="No fields mapped"
                              description="Click 'Map Fields' to add fields to this section"
                              type="info"
                              showIcon
                            />
                          )}
                        </div>
                      </div>
                    </Panel>
                  ))}
                </Collapse>
              </Card>
            </Col>

            {/* Right Panel - Styling Options */}
            <Col span={6}>
              <Card
                title="4. Styling Options"
                bordered={false}
                style={{ backgroundColor: cardBgColor }}
              >
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: labelColor }}>
                      Header Color
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={styling.headerColor}
                        onChange={(e) => setStyling({ ...styling, headerColor: e.target.value })}
                        className="w-12 h-10 border rounded cursor-pointer"
                      />
                      <Input
                        value={styling.headerColor}
                        onChange={(e) => setStyling({ ...styling, headerColor: e.target.value })}
                        size="small"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: labelColor }}>
                      Font Family
                    </label>
                    <Select
                      value={styling.fontFamily}
                      onChange={(value) => setStyling({ ...styling, fontFamily: value })}
                      className="w-full"
                    >
                      <Option value="Arial">Arial</Option>
                      <Option value="Times New Roman">Times New Roman</Option>
                      <Option value="Courier New">Courier New</Option>
                      <Option value="Verdana">Verdana</Option>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: labelColor }}>
                      Font Size: {styling.fontSize}px
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="20"
                      value={styling.fontSize}
                      onChange={(e) => setStyling({ ...styling, fontSize: Number(e.target.value) })}
                      className="w-full"
                    />
                  </div>

                  <Divider />

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm" style={{ color: textColor }}>
                        Show Borders
                      </span>
                      <Switch
                        checked={styling.showBorders}
                        onChange={(checked) => setStyling({ ...styling, showBorders: checked })}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm" style={{ color: textColor }}>
                        Show Page Numbers
                      </span>
                      <Switch
                        checked={styling.showPageNumbers}
                        onChange={(checked) => setStyling({ ...styling, showPageNumbers: checked })}
                      />
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Field Mapper Drawer */}
          <Drawer
            title={
              <Space style={{ color: textColor }}>
                <MapPin size={20} />
                Map API Fields to Section
              </Space>
            }
            placement="right"
            width={500}
            open={fieldMapperVisible}
            onClose={() => setFieldMapperVisible(false)}
            styles={{
              header: { backgroundColor: drawerBgColor, borderBottom: `1px solid ${borderColor}` },
              body: { backgroundColor: drawerBgColor },
            }}
          >
            <div className="space-y-4">
              <Alert
                message="Available API Response Fields"
                description="Select fields from your API response to add to the current section"
                type="info"
                showIcon
              />

              <div className="space-y-2">
                {availableFields.map((field) => {
                  const activeSecObj = sections.find((s) => s.id === activeSection);
                  const isAdded = activeSecObj?.fields.some((f) => f.key === field.key);

                  return (
                    <div
                      key={field.key}
                      className="p-3 border rounded-lg flex items-center justify-between"
                      style={{
                        backgroundColor: isAdded ? (isDark ? '#14532d' : '#f0fdf4') : nestedBgColor,
                        borderColor: isAdded ? (isDark ? '#166534' : '#bbf7d0') : borderColor,
                      }}
                    >
                      <div>
                        <div className="font-medium" style={{ color: textColor }}>
                          {field.label}
                        </div>
                        <div className="text-sm" style={{ color: secondaryTextColor }}>
                          <Tag color="blue" size="small">
                            {field.key}
                          </Tag>
                          <Tag size="small">{field.type}</Tag>
                        </div>
                      </div>
                      <Button
                        type={isAdded ? 'default' : 'primary'}
                        size="small"
                        disabled={isAdded}
                        onClick={() => handleAddField(activeSection, field)}
                        icon={isAdded ? null : <Plus size={14} />}
                      >
                        {isAdded ? 'Added' : 'Add'}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          </Drawer>

          {/* Preview Modal */}
          <Modal
            title={
              <Space style={{ color: textColor }}>
                <Eye size={20} />
                Report Preview
              </Space>
            }
            open={previewVisible}
            onCancel={() => setPreviewVisible(false)}
            width={900}
            styles={{
              header: { backgroundColor: cardBgColor, borderBottom: `1px solid ${borderColor}` },
              body: { backgroundColor: cardBgColor },
              content: { backgroundColor: cardBgColor },
            }}
            footer={[
              <Button key="close" onClick={() => setPreviewVisible(false)}>
                Close
              </Button>,
              <Button
                key="save"
                type="primary"
                icon={<Save size={16} />}
                onClick={handleSaveReport}
                style={{
                  backgroundColor: isDark ? '#0F172A' : defaultColors.primary,
                  borderColor: isDark ? '#0F172A' : defaultColors.primary,
                }}
              >
                Save Configuration
              </Button>,
            ]}
          >
            <div
              dangerouslySetInnerHTML={{ __html: previewHtml }}
              style={{
                backgroundColor: '#fff',
                minHeight: '600px',
                border: `1px solid ${borderColor}`,
              }}
            />
          </Modal>
        </div>
      </Card>
    </div>
  );
};

export default ReportTemplateConfig;
